import React, { useEffect, useState } from 'react';
import { Button, Modal, Toast } from './ui';
import { formatDateTime, formatFileSize, formatPhone } from '@/utils/format';
import { getToken } from '@/utils';
import { downloadOrderAsZip, downloadOrderFilesIndividually } from '@/utils/downloadOrderZip';
import { supabase } from '@/lib/supabase';
import type { Order, OrderMediaFile } from '@shared/types';

// Cache to store batch URLs for 55 minutes
const presignedCache: Record<string, { expiresAt: number, files: any[] }> = {};

interface OrderFilesModalProps {
  isOpen: boolean;
  order: Order | null;
  onClose: () => void;
}

const getMediaLabel = (file: OrderMediaFile): string => {
  switch (file.kind) {
    case 'image':
      return 'Image';
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    case 'document':
    default:
      return 'Document';
  }
};


// Fetch a file's bytes directly from R2 (bypassing backend proxy to save Render bandwidth).
const fetchFileBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch file (HTTP status ${response.status})`);
  }

  return response.blob();
};

// Trigger a browser "Save As" for an in-memory blob.
const saveBlob = (blob: Blob, filename: string): void => {
  const blobUrl = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(blobUrl);
  }
};

const downloadFile = async (url: string, filename: string): Promise<void> => {
  const blob = await fetchFileBlob(url);
  saveBlob(blob, filename);
};

const openPrintOverlay = async (
  presignedUrl: string,
  name: string,
  onReady: () => void,
  showToast: (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning') => void
): Promise<void> => {
  if (!presignedUrl) {
    if (onReady) onReady();
    return;
  }

  const encoded = encodeURIComponent(presignedUrl);
  const googleViewerUrl = 'https://docs.google.com/viewer?url=' + encoded;

  // 1. HANDSHAKE PHASE: Open the tab and inject the Signal Listener
  const viewerWin = window.open('about:blank', '_blank');
  if (viewerWin) {
    viewerWin.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
          <title>Preparing Preview...</title>
          <style>
              body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #f9fafb; color: #374151; transition: background 0.5s; }
              .card { background: white; padding: 2.5rem; border-radius: 16px; box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1); text-align: center; max-width: 420px; border: 1px solid #e5e7eb; }
              .spinner { width: 44px; height: 44px; border: 3px solid #f3f3f3; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 1.5rem; }
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
              h2 { margin: 0 0 0.75rem; font-size: 1.25rem; color: #111827; }
              p { margin: 0; color: #6b7280; font-size: 0.95rem; line-height: 1.6; }
              .status-badge { display: inline-block; margin-top: 1.5rem; padding: 4px 12px; background: #eff6ff; color: #3b82f6; border-radius: 9999px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
          </style>
      </head>
      <body>
          <div class="card">
              <div id="loader" class="spinner"></div>
              <h2 id="msg-title">Connecting to Server</h2>
              <p id="msg-body">Waiting for Google Docs to receive your file bits. Please keep this tab open...</p>
              <div id="status" class="status-badge">Phase 1: Establishing Handshake</div>
          </div>

          <script>
              // DETERMINISTIC SIGNAL LISTENER
              window.addEventListener('message', function(event) {
                  if (event.data && event.data.type === 'PRINTER_READY') {
                      document.getElementById('status').innerText = 'Phase 2: Server Ready';
                      document.getElementById('msg-title').innerText = 'Opening Preview';
                      document.getElementById('msg-body').innerText = 'The rendering engine has responded. Finalizing layout...';
                      document.getElementById('status').style.background = '#ecfdf5';
                      document.getElementById('status').style.color = '#059669';
                      
                      // Buffer 1s after signal to ensure Google's first internal frame is ready
                      setTimeout(function() {
                          window.location.replace(event.data.url);
                      }, 1000);
                  }
              });

              // EMERGENCY FALLBACK (If Dashboard tab is closed/crashes)
              setTimeout(function() {
                  if (window.location.protocol === 'about:') {
                      console.log('Signal timeout: Jumping to viewer anyway.');
                      window.location.replace("${googleViewerUrl}");
                  }
              }, 6500); 
          </script>
      </body>
      </html>
    `);
    viewerWin.document.close();
  }

  // 2. DETERMINISTIC PING PHASE (Main Tab)
  showToast('Preparing Document', 'Talking to preview servers...', 'info');

  try {
    // Wait for both Cloudflare and Google to acknowledge receipt
    await Promise.allSettled([
      fetch(presignedUrl, { method: 'HEAD', mode: 'no-cors' }),
      fetch(googleViewerUrl, { mode: 'no-cors' })
    ]);

    // SEND SIGNAL: Tell the new tab the server has answered the ping
    if (viewerWin && !viewerWin.closed) {
      viewerWin.postMessage({ type: 'PRINTER_READY', url: googleViewerUrl }, '*');
    }
  } catch (err) {
    console.warn('[Print] Signal failed, falling back to timeout navigation');
  }

  if (onReady) onReady();
};

const printFileWithIframe = async (
  fileUrl: string,
  mimeType: string,
  filename: string,
  onReady: () => void,
  showToast: (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning') => void
): Promise<void> => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';

  const CAT_EXCEL = ['xlsx', 'xls', 'csv', 'ods'];
  const CAT_TEXT = ['txt', 'log', 'json', 'xml', 'md', 'html', 'css', 'js', 'ts', 'tsx', 'jsx', 'yml', 'yaml', 'ini', 'conf'];
  const CAT_DL_OFF = ['doc', 'rtf', 'odt', 'odp'];
  const CAT_VECTOR = ['ai', 'eps', 'psd', 'svg'];
  const CAT_CAD = ['dwg', 'dxf'];
  const CAT_DOCX = ['docx', 'ppt', 'pptx', 'tif', 'tiff'];

  // 1. DOCX — Google Docs Viewer (Pre-warming for clean previews)
  if (CAT_DOCX.includes(ext)) {
    await openPrintOverlay(fileUrl, filename, onReady, showToast);
    return;
  }

  // 1. LEGACY OFFICE / DESIGN / CAD — download prompt
  if (CAT_DL_OFF.includes(ext) || CAT_VECTOR.includes(ext) || CAT_CAD.includes(ext)) {
    onReady(); // restore button before showing confirm dialog
    const app = ext === 'doc' ? 'Microsoft Word'
      : (ext === 'ppt' || ext === 'pptx') ? 'Microsoft PowerPoint'
      : 'the appropriate application';
    if (window.confirm(`Direct printing not supported for .${ext}.\n\nClick OK to download, then print from ${app}.`)) {
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    return;
  }

  // 2. EXCEL — same-tab hidden iframe
  if (CAT_EXCEL.includes(ext)) {
    const processExcel = () => {
      fetch(fileUrl)
        .then(r => r.arrayBuffer())
        .then(buf => {
          const XLSX = (window as any).XLSX;
          const wb = XLSX.read(buf, { type: 'array' });
          const sheetCount = wb.SheetNames.length;
          let sheetsHtml = '';
          wb.SheetNames.forEach((name: string, i: number) => {
            const safeName = name.replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const tableHtml = XLSX.utils.sheet_to_html(wb.Sheets[name], { editable: false });
            const titleHtml = sheetCount > 1 ? `<div class="sheet-title">&#128203; ${safeName}</div>` : '';
            sheetsHtml += `<div class="sheet ${i > 0 ? 'pb' : ''}">${titleHtml}<div class="wrap">${tableHtml}</div></div>`;
          });

          const iframe = document.createElement('iframe');
          iframe.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:-1;opacity:0;pointer-events:none;border:none;';
          document.body.appendChild(iframe);

          const iDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!iDoc) {
            throw new Error('Could not access iframe document.');
          }
          iDoc.open();
          iDoc.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
          *{box-sizing:border-box;} body{margin:0;padding:20px;font-family:'Calibri',Arial,sans-serif;background:#fff;}
          .sheet{width:100%;margin-bottom:20px;}
          .sheet-title{font-size:13pt;font-weight:bold;color:#1a6e31;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #1a6e31;}
          .wrap{overflow-x:auto;} table{border-collapse:collapse;width:100%;font-size:10pt;}
          td,th{border:1px solid #ccc;padding:4px 8px;white-space:nowrap;} th{background:#e8f5e9;font-weight:bold;}
          tr:nth-child(even) td{background:#f9fafb;}
          @media print{body{padding:0;} .sheet{margin:0;} .pb{page-break-before:always;} table{font-size:9pt;} td,th{padding:3px 6px;}}
      </style></head><body>
          ${sheetsHtml}
      </body></html>`);
          iDoc.close();

          let triggered = false;
          const callPrint = () => {
            if (triggered) return;
            triggered = true;
            onReady(); // restore spinner RIGHT before dialog fires
            try { iframe.contentWindow?.print(); } catch (e) { }
            setTimeout(() => { if (iframe.parentNode) document.body.removeChild(iframe); }, 120000);
          };
          iframe.onload = callPrint;
          setTimeout(callPrint, 500); // fallback if onload already fired
        })
        .catch(err => {
          console.error('[Print] SheetJS Excel error:', err);
          onReady(); // always restore button
          showToast('Excel Error', 'Could not render file. Download and print from Excel.', 'error');
        });
    };

    if (typeof (window as any).XLSX === 'undefined') {
      showToast('Loading Excel Engine...', 'First time setup, please wait 3 seconds.', 'info');
      const script = document.createElement('script');
      script.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
      script.onload = () => processExcel();
      script.onerror = () => {
        onReady();
        showToast('Engine Error', 'Please download the file.', 'error');
      };
      document.head.appendChild(script);
    } else {
      processExcel();
    }

    return;
  }

  // 3. TEXT FILES — same-tab hidden iframe
  if (CAT_TEXT.includes(ext)) {
    fetch(fileUrl)
      .then(r => r.text())
      .then(text => {
        const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:-1;opacity:0;pointer-events:none;border:none;';
        document.body.appendChild(iframe);

        const iDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iDoc) {
          throw new Error('Could not access iframe document.');
        }
        iDoc.open();
        iDoc.write(`<!DOCTYPE html><html><head><style>
            *{box-sizing:border-box;} body{margin:1in;font-family:'Consolas','Courier New',monospace;background:#fff;}
            pre{white-space:pre-wrap;word-wrap:break-word;font-size:11pt;line-height:1.6;color:#222;margin:0;}
            @media print{body{margin:.5in;} pre{font-size:10pt;}}
        </style></head><body><pre>${safe}</pre></body></html>`);
        iDoc.close();

        let triggered = false;
        const callPrint = () => {
          if (triggered) return;
          triggered = true;
          onReady(); // restore spinner RIGHT before dialog fires
          try { iframe.contentWindow?.print(); } catch (e) { }
          setTimeout(() => { if (iframe.parentNode) document.body.removeChild(iframe); }, 120000);
        };
        iframe.onload = callPrint;
        setTimeout(callPrint, 300); // fallback
      })
      .catch(err => {
        console.error('[Print] Text fetch error:', err);
        onReady();
        showToast('File Error', 'Could not load text file for printing.', 'error');
      });
    return;
  }

  // 4. PDF & IMAGES — Existing logic
  const isPdf = mimeType.toLowerCase() === 'application/pdf';
  const supportedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  const isImage = supportedImageTypes.includes(mimeType.toLowerCase());

  if (isPdf || isImage) {
    // Fetch the pre-signed R2 URL directly, bypassing the backend proxy
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch file for printing (HTTP status ${response.status})`);
    }

    // Clean up previous print iframe if it exists
    const existingIframe = document.getElementById('print-iframe') as HTMLIFrameElement;
    if (existingIframe) {
      if (existingIframe.dataset.blobUrl) {
        window.URL.revokeObjectURL(existingIframe.dataset.blobUrl);
      }
      document.body.removeChild(existingIframe);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    const iframe = document.createElement('iframe');
    iframe.id = 'print-iframe';
    iframe.dataset.blobUrl = blobUrl;
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.pointerEvents = 'none';

    document.body.appendChild(iframe);

    if (isPdf) {
      iframe.src = blobUrl;
      await new Promise<void>((resolve, reject) => {
        const handleLoad = () => {
          iframe?.removeEventListener('load', handleLoad);
          iframe?.removeEventListener('error', handleError);
          resolve();
        };
        const handleError = () => {
          iframe?.removeEventListener('load', handleLoad);
          iframe?.removeEventListener('error', handleError);
          reject(new Error('Failed to load PDF inside iframe.'));
        };
        iframe?.addEventListener('load', handleLoad);
        iframe?.addEventListener('error', handleError);
        
        // Timeout fallback
        setTimeout(() => resolve(), 10000);
      });
    } else {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) {
        throw new Error('Could not access iframe document.');
      }

      doc.open();
      doc.write(`
        <html>
        <head>
        <style>
        html,body{
          margin:0;
          padding:0;
          height: 100%;
        }
        img{
          width:100%;
          height:100%;
          max-height: 100vh;
          object-fit:contain;
          page-break-inside: avoid;
        }
        @media print{
          body{
            margin:0;
          }
          @page {
            margin: 0;
          }
        }
        </style>
        </head>
        <body>
        <img id="print-image" src="${blobUrl}" />
        </body>
        </html>
      `);
      doc.close();

      const imgElement = doc.getElementById('print-image') as HTMLImageElement | null;
      if (!imgElement) {
        throw new Error('Image element not found in iframe document.');
      }

      await new Promise<void>((resolve, reject) => {
        if (imgElement.complete) {
          resolve();
        } else {
          imgElement.onload = () => resolve();
          imgElement.onerror = () => reject(new Error('Failed to load image inside iframe.'));
        }
      });
    }

    const iframeWindow = iframe.contentWindow;
    if (!iframeWindow) {
      throw new Error('Could not access iframe window.');
    }

    iframeWindow.focus();
    
    // For PDFs, wait a tiny bit to ensure the browser PDF plugin is fully initialized
    if (isPdf) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    onReady(); // restore spinner RIGHT before dialog fires

    try {
      iframeWindow.print();
    } catch (err) {
      console.error('Print error:', err);
    }
    return;
  }

  // 5. FALLBACK — open in new tab
  onReady();
  window.open(fileUrl, '_blank');
};

export const OrderFilesModal: React.FC<OrderFilesModalProps> = ({ isOpen, order, onClose }) => {
  const [printingFileId, setPrintingFileId] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [isDownloadingEach, setIsDownloadingEach] = useState(false);
  const [isPrintingAllImages, setIsPrintingAllImages] = useState(false);
  const [isPrintingAllPdfs, setIsPrintingAllPdfs] = useState(false);
  const [isLoadingUrls, setIsLoadingUrls] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<any[]>([]);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; message: string } | null>(null);

  const isActionInProgress = printingFileId !== null || downloadingFileId !== null || isDownloadingAll || isDownloadingEach || isPrintingAllImages || isPrintingAllPdfs;

  // Fetch or load cached batch URLs when modal opens
  useEffect(() => {
    if (!isOpen || !order) {
      setMediaFiles([]);
      return;
    }

    // 1. Optimistic Rendering: Instantly create the file list from order details without URLs
    const initialFiles = (order.order_details || []).map((meta: any, index: number) => ({
      id: `${order.id}-file-${index}`,
      name: meta.fileName,
      url: null, // URLs are fetched asynchronously
      kind: meta.mimeType.startsWith('image/') ? 'image' as const
            : meta.mimeType.startsWith('video/') ? 'video' as const
            : meta.mimeType.startsWith('audio/') ? 'audio' as const
            : 'document' as const,
      mime_type: meta.mimeType,
      size_bytes: meta.file_size || 0,
      page_count: meta.page_count,
      uploaded_at: meta.receivedAt,
      isPrinted: meta.isPrinted || false,
    }));

      const fetchUrls = async () => {
      // Check cache first (valid for 55 mins)
      const cached = presignedCache[order.id];
      // Only use cache if it hasn't expired AND the number of cached files matches the current order details length
      // If we don't have order_details (due to dashboard view optimization), we fallback to comparing with file_count if available.
      const expectedCount = order.order_details?.length ?? (order as any).file_count ?? 0;
      if (cached && cached.expiresAt > Date.now() && cached.files.length === expectedCount) {
        // Temporary fix: Invalidate cache if it was stored without metadata (due to previous bug)
        if (cached.files.length > 0 && !cached.files[0].uploaded_at) {
          // Cache is invalid, proceed to fetch
        } else {
          setMediaFiles(cached.files);
          return;
        }
      }

      // 2. Set optimistic state so the UI instantly renders the cards
      setMediaFiles(initialFiles);
      setIsLoadingUrls(true);
      try {
        // If order_details is missing (because we fetched from a view), we must fetch it now for the metadata
        let orderDetails = order.order_details;
        if (!orderDetails) {
           const { data } = await supabase
             .from('orders').select('order_details').eq('id', order.id).single();
           orderDetails = data?.order_details || [];
        }

        const token = getToken();
        const response = await fetch(`${import.meta.env.VITE_API_URL}/order-urls/${order.shop_id}/${order.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error('Failed to fetch file URLs');
        }

        const data = await response.json();
        const fetchedFiles = data.files || [];

        // Map the backend data to frontend mediaFiles structure
        const formattedFiles = fetchedFiles.map((file: any) => {
          const originalMeta = orderDetails[file.index] || {} as any;
          return {
            id: `${order.id}-file-${file.index}`,
            name: file.fileName,
            url: file.fileUrl, // Direct S3 signed URL
            kind: file.mimeType.startsWith('image/') ? 'image' as const
                  : file.mimeType.startsWith('video/') ? 'video' as const
                  : file.mimeType.startsWith('audio/') ? 'audio' as const
                  : 'document' as const,
            mime_type: file.mimeType,
            size_bytes: originalMeta.file_size || 0,
            page_count: originalMeta.page_count,
            uploaded_at: originalMeta.receivedAt || '',
            isPrinted: originalMeta.isPrinted || false,
          };
        });

        // Store in cache for 55 minutes
        presignedCache[order.id] = {
          expiresAt: Date.now() + 55 * 60 * 1000,
          files: formattedFiles
        };

        setMediaFiles(formattedFiles);
      } catch (err) {
        console.error('Failed to load files:', err);
        setToast({ type: 'error', message: 'Failed to load file URLs. Please try again.' });
        setMediaFiles([]);
      } finally {
        setIsLoadingUrls(false);
      }
    };

    fetchUrls();
  }, [isOpen, order]);

  const handlePrint = async (fileId: string, directUrl: string, mimeType: string, filename: string) => {
    if (isActionInProgress) return;
    
    setPrintingFileId(fileId);
    try {
      // Use the direct cached signed URL to print
      await printFileWithIframe(
        directUrl, 
        mimeType, 
        filename,
        () => setPrintingFileId(null),
        (title, message, type) => setToast({ type, message: `${title}: ${message}` })
      );

      // Mark as printed in database
      const fileIndexStr = fileId.split('-file-')[1];
      if (fileIndexStr !== undefined && order) {
        const fileIndex = parseInt(fileIndexStr, 10);
        const token = getToken();
        const response = await fetch(`${import.meta.env.VITE_API_URL}/orders/${order.id}/files/${fileIndex}/printed`, {
          method: 'POST',
          headers: token ? { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } : { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });

        if (response.ok) {
          // Update local state instantly so the tick appears
          setMediaFiles(prev => prev.map(f => f.id === fileId ? { ...f, isPrinted: true } : f));
          // Clear cache so subsequent rendering fetches updated order details
          if (presignedCache[order.id]) {
            delete presignedCache[order.id];
          }
        }
      }
    } catch (err) {
      console.error('Printing failed:', err);
      setToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'An error occurred while preparing print.',
      });
    } finally {
      setPrintingFileId(null);
    }
  };
  const handlePrintAll = async (type: 'images' | 'pdfs') => {
    if (isActionInProgress || !order) return;

    // 1. Filter target files
    let targetFiles: any[] = [];
    if (type === 'images') {
      targetFiles = mediaFiles.filter((f: any) => f.mime_type?.startsWith('image/'));
    } else {
      targetFiles = mediaFiles.filter((f: any) => f.mime_type === 'application/pdf');
    }

    if (targetFiles.length === 0) {
      setToast({ type: 'info', message: `No compatible ${type} found in this order.` });
      return;
    }

    // Wait until URLs are loaded
    if (targetFiles.some((f) => !f.url)) {
      setToast({ type: 'info', message: 'Files are still loading. Please try again in a moment.' });
      return;
    }

    // 2. Check total size limit (150 MB)
    const totalSize = targetFiles.reduce((acc, f) => acc + (f.size_bytes || 0), 0);
    const MAX_MERGE_SIZE = 150 * 1024 * 1024; // 150 MB

    if (totalSize > MAX_MERGE_SIZE) {
      setToast({ 
        type: 'warning', 
        message: `Total size (${(totalSize / (1024 * 1024)).toFixed(1)} MB) exceeds 150 MB limit. Please print files individually or download as ZIP.` 
      });
      return;
    }

    if (type === 'images') setIsPrintingAllImages(true);
    else setIsPrintingAllPdfs(true);

    try {
      // 3. Lazy load pdf-lib dynamically to keep initial bundle size small
      const { PDFDocument } = await import('pdf-lib');
      const masterPdf = await PDFDocument.create();

      // 4. Download files in parallel using direct R2 URLs
      const fileBuffers = await Promise.all(
        targetFiles.map(async (file) => {
          const res = await fetch(file.url);
          if (!res.ok) throw new Error(`Failed to download ${file.name}`);
          return res.arrayBuffer();
        })
      );

      // 5. Merge PDF pages or embed images into pages
      if (type === 'pdfs') {
        for (const buf of fileBuffers) {
          const pdf = await PDFDocument.load(buf, { ignoreEncryption: true });
          const copiedPages = await masterPdf.copyPages(pdf, pdf.getPageIndices());
          copiedPages.forEach((page) => masterPdf.addPage(page));
        }
      } else {
        // Images
        for (let i = 0; i < fileBuffers.length; i++) {
          const buf = fileBuffers[i];
          const file = targetFiles[i];
          let image;
          if (file.mime_type?.includes('png')) {
            image = await masterPdf.embedPng(buf);
          } else {
            image = await masterPdf.embedJpg(buf);
          }

          // A4 dimensions: 595.28 x 841.89 points
          const page = masterPdf.addPage([595.28, 841.89]);
          const { width, height } = page.getSize();
          const imgDims = image.scaleToFit(width - 40, height - 40); // 20pt margin padding

          page.drawImage(image, {
            x: (width - imgDims.width) / 2,
            y: (height - imgDims.height) / 2,
            width: imgDims.width,
            height: imgDims.height,
          });
        }
      }

      // 6. Generate local blob URL
      const mergedPdfBytes = await masterPdf.save();
      const blob = new Blob([mergedPdfBytes as any], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      // 7. Clean up previous print iframe if it exists
      const existingIframe = document.getElementById('print-iframe') as HTMLIFrameElement;
      if (existingIframe) {
        if (existingIframe.dataset.blobUrl) {
          window.URL.revokeObjectURL(existingIframe.dataset.blobUrl);
        }
        document.body.removeChild(existingIframe);
      }

      // 8. Open hidden iframe print dialog
      const iframe = document.createElement('iframe');
      iframe.id = 'print-iframe';
      iframe.dataset.blobUrl = blobUrl;
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.pointerEvents = 'none';
      iframe.src = blobUrl;

      document.body.appendChild(iframe);

      await new Promise<void>((resolve, reject) => {
        const handleLoad = () => {
          iframe?.removeEventListener('load', handleLoad);
          iframe?.removeEventListener('error', handleError);
          resolve();
        };
        const handleError = () => {
          iframe?.removeEventListener('load', handleLoad);
          iframe?.removeEventListener('error', handleError);
          reject(new Error('Failed to load merged PDF inside print frame.'));
        };
        iframe?.addEventListener('load', handleLoad);
        iframe?.addEventListener('error', handleError);
        
        // Timeout fallback
        setTimeout(() => resolve(), 10000);
      });

      const iframeWindow = iframe.contentWindow;
      if (!iframeWindow) {
        throw new Error('Could not access print window.');
      }

      iframeWindow.focus();
      await new Promise(resolve => setTimeout(resolve, 500));

      if (type === 'images') setIsPrintingAllImages(false);
      else setIsPrintingAllPdfs(false);

      iframeWindow.print();
    } catch (err) {
      console.error(`Print All (${type}) failed:`, err);
      setToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to merge files for printing.',
      });
      if (type === 'images') setIsPrintingAllImages(false);
      else setIsPrintingAllPdfs(false);
    }
  };
  const handleDownload = async (fileId: string, directUrl: string, filename: string) => {
    if (isActionInProgress) return;
    setDownloadingFileId(fileId);
    try {
      // Use the direct cached signed URL to download
      await downloadFile(directUrl, filename);
    } catch (err) {
      console.error('Download failed:', err);
      setToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'An error occurred during file download.',
      });
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleDownloadEach = async () => {
    if (isActionInProgress || mediaFiles.length === 0) return;
    if (mediaFiles.some((f) => !f.url)) {
      setToast({ type: 'info', message: 'Files are still loading. Please try again in a moment.' });
      return;
    }
    setIsDownloadingEach(true);
    try {
      const count = await downloadOrderFilesIndividually(
        order,
        mediaFiles.map((f) => ({ name: f.name, url: f.url }))
      );
      setToast({ type: 'success', message: `Downloaded ${count} files.` });
    } catch (err) {
      console.error('Download each failed:', err);
      setToast({ type: 'error', message: 'An error occurred while downloading files.' });
    } finally {
      setIsDownloadingEach(false);
    }
  };

  const handleDownloadAll = async () => {
    if (isActionInProgress || mediaFiles.length === 0) return;
    // Wait until the signed URLs have loaded before zipping.
    if (mediaFiles.some((f) => !f.url)) {
      setToast({ type: 'info', message: 'Files are still loading. Please try again in a moment.' });
      return;
    }
    setIsDownloadingAll(true);
    try {
      // Reuse the already-loaded signed URLs so we skip a round-trip.
      const count = await downloadOrderAsZip(
        order,
        mediaFiles.map((f) => ({ name: f.name, url: f.url }))
      );
      setToast({ type: 'success', message: `Downloaded ${count} files as a ZIP.` });
    } catch (err) {
      console.error('Download all failed:', err);
      setToast({ type: 'error', message: 'An error occurred while building the ZIP.' });
    } finally {
      setIsDownloadingAll(false);
    }
  };

  if (!order) {
    return <Modal isOpen={isOpen} title="Files" onClose={onClose} size="lg"><div /></Modal>;
  }

  const headerContent = (
    <div className="flex items-center gap-6 pr-4">
      <div className="flex items-center gap-2 text-slate-800">
        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
        </svg>
        <span>{formatPhone(order.customer_phone)}</span>
      </div>
      
      {mediaFiles.length > 0 && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleDownloadEach}
            disabled={isActionInProgress}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors border shadow-sm ${
              isActionInProgress
                ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`}
            title="Download each file separately"
          >
            {isDownloadingEach ? (
              <svg className="h-3.5 w-3.5 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            {isDownloadingEach ? 'Downloading…' : 'Download Each'}
          </button>

          <button
            onClick={handleDownloadAll}
            disabled={isActionInProgress}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors border shadow-sm ${
              isActionInProgress
                ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-slate-800 border-slate-800 text-white hover:bg-slate-700'
            }`}
            title="Download all files as a single ZIP"
          >
            {isDownloadingAll ? (
              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            {isDownloadingAll ? 'Zipping…' : 'Download All (ZIP)'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} title={headerContent} onClose={onClose} size="lg">
      {mediaFiles.length === 0 && !isLoadingUrls ? (
        <div className="text-center py-8 text-slate-500">
          <p>No valid files found for this order.</p>
        </div>
      ) : (
        <div className="flex flex-col h-full relative">
          <div className="grid gap-3">
          {mediaFiles.map((file, index) => {
          const isFilePrinting = printingFileId === file.id;
          const isFileDownloading = downloadingFileId === file.id;

          return (
            <div key={file.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-4 pt-4 pb-3 sm:px-5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
                    {getMediaLabel(file)} #{index + 1}
                  </p>
                  <h3 className="mt-1 truncate text-base font-semibold text-slate-950">{file.name}</h3>
                  <p className="mt-1 text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                    <span>{file.mime_type} {file.size_bytes ? `• ${formatFileSize(file.size_bytes)}` : ''}</span>
                    {file.isPrinted && (
                      <span className="inline-flex items-center text-emerald-600 ml-1" title="Printed">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="4.5" viewBox="0 0 24 24">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </p>
                </div>

                <div className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                  {file.page_count === undefined ? 'Unknown Pages' 
                    : file.page_count === -1 ? 'Unsupported' 
                    : file.page_count === 0 ? 'Calculating...' 
                    : `${file.page_count} Page${file.page_count === 1 ? '' : 's'}`}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 px-4 py-2 sm:px-5">
                <div className="text-xs text-slate-400">
                  Uploaded: <span className="font-medium text-slate-600">{formatDateTime(file.uploaded_at)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (!file.url) return;
                      const safePhone = order.customer_phone.replace(/\D/g, '');
                      const structuredName = `${safePhone}_file${index + 1}_${file.name}`;
                      handleDownload(file.id, file.url, structuredName);
                    }}
                    disabled={isActionInProgress || !file.url}
                    className={`h-10 w-10 flex items-center justify-center rounded-lg border-2 transition-colors ${
                      isActionInProgress || !file.url
                        ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                        : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                    }`}
                    title="Download file"
                  >
                    {isFileDownloading || !file.url ? (
                      <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (!file.url) return;
                      handlePrint(file.id, file.url, file.mime_type, file.name)
                    }}
                    disabled={isActionInProgress || !file.url}
                    className={`px-3 h-10 flex items-center justify-center gap-1.5 rounded-lg border-2 transition-colors ${
                      isActionInProgress || !file.url
                        ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                        : 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100'
                    }`}
                    title="Print file"
                  >
                    {isFilePrinting || !file.url ? (
                      <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <polyline points="6 9 6 2 18 2 18 9" />
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                        <rect x="6" y="14" width="12" height="8" />
                      </svg>
                    )}
                    <span className="text-sm font-semibold">Print</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        </div>
        
        {/* Sticky Print All Footer */}
        <div className="sticky bottom-0 -mx-4 -mb-4 mt-6 p-4 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.05)] flex flex-wrap items-center justify-end gap-3 rounded-b-lg">
          {mediaFiles.some(f => f.kind === 'image') && (
            <button
              onClick={() => handlePrintAll('images')}
              disabled={isActionInProgress}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                isPrintingAllImages ? 'bg-blue-600 text-white shadow-blue-200 ring-2 ring-blue-600 ring-offset-2' 
                : isActionInProgress ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-50 hover:shadow-md active:scale-[0.98]'
              }`}
            >
              {isPrintingAllImages ? (
                <>
                  <svg className="h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Merging images... please wait</span>
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                  </svg>
                  <span>Print All (Images)</span>
                </>
              )}
            </button>
          )}

          {mediaFiles.some(f => f.mime_type === 'application/pdf') && (
            <button
              onClick={() => handlePrintAll('pdfs')}
              disabled={isActionInProgress}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                isPrintingAllPdfs ? 'bg-rose-600 text-white shadow-rose-200 ring-2 ring-rose-600 ring-offset-2' 
                : isActionInProgress ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white border-2 border-rose-600 text-rose-700 hover:bg-rose-50 hover:shadow-md active:scale-[0.98]'
              }`}
            >
              {isPrintingAllPdfs ? (
                <>
                  <svg className="h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Merging PDFs... please wait</span>
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" />
                  </svg>
                  <span>Print All (PDFs)</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
      )}

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </Modal>
  );
};

export default OrderFilesModal;